"use client";

import { infoPageHref } from "@/shared/routes";
import {
    Footer as UIFooter,
    SmartImage,
    Text,
    type FooterAside,
    type FooterContact,
    type FooterContactChannel,
    type FooterLinkGroup,
    type FooterVariantKey,
} from "@dionatanbig2be/big2be-ui";
import { useAppSetting, useAvailableStores, useFooterGroups, useResolvedStoreId } from "../hooks/useBootstrap";
import { THEME_SCOPE_NAMES } from "@dionatanbig2be/big2be-ui/core";
import { useTheme } from "@/theme";
import { isEphemeralMediaUrl, resolveMediaUrl } from "@/shared/helpers/media";
import { formatBrPhone } from "@/shared/helpers/phone";
import type {
    AppAssetsContent,
    AppIdentityContent,
    FooterBlockSocial,
    FooterContactBlock,
    FooterLegalBlock,
    FooterPaymentsBlock,
} from "@/shared/settings";
import { applyMask } from "@dionatanbig2be/big2be-ui/utils";
import type { StoreInfoDto } from "@/modules/store/types/store.dto";

const DEVELOPER_URL = "https://big2be.tech/";

/**
 * Selos fixos de todas as lojas: o tráfego de todo tenant passa pela Cloudflare, sob HTTPS.
 * Todas as imagens do rodapé vão com `loading="lazy"`: sem isso, o SmartImage
 * "plain" sai sem o atributo e o navegador baixa os selos junto com o topo da
 * página, disputando banda com a imagem do LCP.
 */
const SECURITY_ASIDE: FooterAside = {
    title: "Segurança",
    content: (
        <div className="flex flex-wrap items-center justify-center gap-2 md:justify-start">
            <SmartImage
                src="/footer/cloudflare-protected-ac066a6e.png"
                alt="Protegido por Cloudflare"
                width={124}
                height={28}
                className="h-7 w-auto rounded-[2px]"
                loading="lazy"
            />
            <SmartImage
                src="/footer/ssl-seguro-996d04c6.jpg"
                alt="Site seguro com certificado SSL"
                width={92}
                height={28}
                className="h-7 w-auto rounded-[2px]"
                loading="lazy"
            />
        </div>
    ),
};

/** Rótulo de cada bandeira. A chave também nomeia o arquivo em `public/footer`. */
const PAYMENT_BRAND_LABEL: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    elo: "Elo",
    hipercard: "Hipercard",
    amex: "American Express",
    pix: "Pix",
    boleto: "Boleto",
};

/**
 * Bloco gravado na identidade visual → faixa de bandeiras. Chave desconhecida é
 * descartada: ela não tem arquivo em `public/footer` e viraria imagem quebrada.
 */
function paymentsOf(block: FooterPaymentsBlock | undefined): FooterAside | undefined {
    if (!block || block.enabled === false) return undefined;

    const brands = (block.brands ?? []).filter((brand) => brand in PAYMENT_BRAND_LABEL);
    if (brands.length === 0) return undefined;

    return {
        title: block.title?.trim() || undefined,
        content: (
            <div className="flex flex-wrap items-center justify-center gap-2">
                {brands.map((brand) => (
                    <SmartImage
                        key={brand}
                        src={`/footer/${brand}.webp`}
                        alt={PAYMENT_BRAND_LABEL[brand]}
                        width={42}
                        height={28}
                        className="h-6 w-auto rounded-[2px]"
                        loading="lazy"
                    />
                ))}
            </div>
        ),
    };
}

const DEVELOPER_CREDIT = (
    <a
        href={DEVELOPER_URL}
        target="_blank"
        rel="noopener"
        className="flex items-center gap-2 opacity-60 transition-opacity hover:opacity-100"
    >
        <Text variant="paragraph" className="m-0 text-[11px]">
            Desenvolvido por
        </Text>
        <SmartImage src="/footer/big2be.webp" alt="Big2be" width={56} height={20} loading="lazy" />
    </a>
);

/** Fallback do título: identidade salva antes do campo existir, ou lojista que apagou o texto. */
const DEFAULT_CONTACT_TITLE = "Atendimento";

/**
 * Telefone local (DDD + número) → formato de link, com DDI. O portal aplica
 * máscara de telefone brasileiro nos dois campos, então o valor nunca chega
 * com `+55` — daí o 55 ser somado aqui em vez de esperado do lojista.
 */
const brDigits = (value: string) => `55${value.replace(/\D/g, "")}`;

/** Redes do cadastro da loja no mesmo formato do bloco; campo vazio fica de fora. */
function storeSocial(store: StoreInfoDto): FooterBlockSocial[] {
    return (
        [
            ["whatsapp", store.whatsapp],
            ["instagram", store.instagram],
            ["facebook", store.facebook],
            ["tiktok", store.tiktok],
        ] as const
    ).flatMap(([network, value]) => (value ? [{ network, value }] : []));
}

/**
 * Bloco gravado na identidade visual → coluna de atendimento do widget. Os
 * canais vêm de UMA fonte só: o cadastro da loja selecionada, quando o bloco
 * está em "Dados da loja", ou os campos do próprio bloco ("Personalizado", ou
 * sem loja selecionada). Título e liga/desliga são sempre do bloco.
 */
function contactOf(
    block: FooterContactBlock | undefined,
    store: StoreInfoDto | undefined
): FooterContact | undefined {
    if (block?.enabled === false) return undefined;

    const fromStore = block?.source === "store" && store ? store : undefined;
    // O cadastro guarda o telefone só com dígitos; o bloco, já formatado pelo portal.
    const phone = (fromStore ? formatBrPhone(fromStore.phone) : block?.phone)?.trim();
    const email = (fromStore ? fromStore.email : block?.email)?.trim();
    const socialItems = fromStore ? storeSocial(fromStore) : (block?.social ?? []);

    const channels: FooterContactChannel[] = [
        ...(phone
            ? [{ kind: "phone" as const, value: phone, href: `tel:+${brDigits(phone)}` }]
            : []),
        ...(email ? [{ kind: "email" as const, value: email, href: `mailto:${email}` }] : []),
    ];

    // `app_visual_identity` é JSON livre — o backend não valida, então o que
    // chega aqui não é confiável. Só vira link o que é http(s) absoluto.
    const social = socialItems
        .filter((item) => item.value?.trim())
        .map((item) => ({
            network: item.network,
            url:
                item.network === "whatsapp"
                    ? `https://wa.me/${brDigits(item.value)}`
                    : item.value.trim(),
        }))
        .filter((item) => /^https?:\/\//i.test(item.url));

    if (channels.length === 0 && social.length === 0) return undefined;

    return { title: block?.title?.trim() || DEFAULT_CONTACT_TITLE, channels, social };
}

/**
 * Linha "Razão social · CNPJ … · endereço" da loja selecionada, quando o bloco
 * de dados legais está ligado. Desligado ou sem loja, nada — o texto livre do
 * rodapé segue sozinho, como antes da opção existir.
 */
function legalLineOf(block: FooterLegalBlock | undefined, store: StoreInfoDto | undefined): string | undefined {
    if (block?.enabled !== true || !store) return undefined;

    const parts = [
        store.legal_name?.trim(),
        store.cnpj ? `CNPJ ${applyMask("cnpj", store.cnpj)}` : undefined,
        store.address?.trim(),
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(" · ") : undefined;
}

interface FooterProps {
    /** Footer reduzido a uma faixa fina (links compactos + copyright), usado em checkout/carrinho etc. */
    simplified?: boolean;
}

/** Adapter do Footer do big2be-ui: resolve logo, descrição, grupos e copyright do bootstrap. */
export function Footer({ simplified = false }: FooterProps = {}) {
    const theme = useTheme();
    // INTENCIONAL, não é map quebrado: a lib só expõe `variant-1`, então todo
    // `structure.footer.template` cai nela. Virar map quando houver mais variantes.
    const variant: FooterVariantKey = "variant-1";
    const identity = useAppSetting<AppIdentityContent>("app_identity");
    const assets = useAppSetting<AppAssetsContent>("app_assets");
    const rawGroups = useFooterGroups();
    // Loja resolvida no SSR (e refeita no `router.refresh()` da troca de loja):
    // com ela o rodapé já sai certo no HTML, sem esperar a hidratação.
    const storeId = useResolvedStoreId();
    const store = useAvailableStores().find((s) => s.store_id === storeId);

    // blob:/data: do editor do portal não é mídia — cai no app_assets.
    const themeLogo = isEphemeralMediaUrl(theme?.graphics?.logo)
        ? undefined
        : theme?.graphics?.logo;
    const logoPath = themeLogo ?? assets?.logo?.url;
    const copyright = identity?.title
        ? `© ${new Date().getFullYear()} ${identity.title}. Todos os direitos reservados.`
        : undefined;

    const blocks = theme?.structure?.footer?.blocks;
    const contactBlock = blocks?.find((block) => block.type === "contact") as
        | FooterContactBlock
        | undefined;
    const contact = contactOf(contactBlock, store);
    const payments = paymentsOf(
        blocks?.find((block) => block.type === "payment_methods") as FooterPaymentsBlock | undefined
    );
    // Dados legais da loja na primeira linha; o texto livre (Configurações) logo abaixo.
    const legalLine = legalLineOf(
        blocks?.find((block) => block.type === "legal_info") as FooterLegalBlock | undefined,
        store
    );
    const freeText = [legalLine, identity?.footer_text?.trim()].filter(Boolean).join("\n") || undefined;

    const groups: FooterLinkGroup[] = rawGroups.map((group) => ({
        key: String(group.groupId),
        title: group.name,
        links: group.items.map((item) => ({
            key: String(item.infoPageId),
            label: item.title,
            href: infoPageHref(item.pageKey ?? item.infoPageId),
        })),
    }));

    return (
        // Tema escopado do widget. `display: contents` não gera caixa, mas cascateia os tokens.
        <div className="contents" data-theme={THEME_SCOPE_NAMES.footer}>
            <UIFooter
                variant={variant}
                simplified={simplified}
                logo={{
                    src: resolveMediaUrl(logoPath) ?? undefined,
                    alt: assets?.logo?.alt ?? "Logo",
                }}
                description={identity?.short_description ?? undefined}
                freeText={freeText}
                groups={groups}
                aside={SECURITY_ASIDE}
                payments={payments}
                contact={contact}
                copyright={copyright}
                bottomSlot={DEVELOPER_CREDIT}
            />
        </div>
    );
}
